import React, { useState, useEffect } from "react"
import { postJson } from "../../helpers/http"
import { API_ENDPOINTS } from "../../helpers/api"
import { LOGIN_REQUIRED_TITLE } from "../../helpers/auth"

const MIN_PROMPT_CHARS = 3
const MAX_PROMPT_CHARS = 150

export const aiTierLabel = (aiTier) => (aiTier === "smart" ? "Smart" : "Quick")

const HintButton = ({ active, onClick, label }) => (
  <button
    type="button"
    className={`intelligence-hint-btn${active ? " active" : ""}`}
    onClick={onClick}
    aria-label={label}
  >
    i
  </button>
)

const nonWhitespaceLength = (value) => value.replace(/\s/g, "").length

const fetchLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null)
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => resolve({ latitude, longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000 }
    )
  })

const AiPrompt = ({
  onChoicesGenerated,
  onPromptUsed,
  onMarkersReceived,
  maxChoices,
  aiTier,
  onAiTierChange,
  prompt,
  onPromptChange,
  useLocation = false,
  onUseLocationChange,
  isLoggedIn = false,
  currentUser = null,
  onPremiumClick,
}) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [dotCount, setDotCount] = useState(1)
  const [error, setError] = useState(null)
  const [warning, setWarning] = useState(null)
  const [showTierHint, setShowTierHint] = useState(false)
  const [showLocationHint, setShowLocationHint] = useState(false)

  useEffect(() => {
    setError(null)
    setWarning(null)
  }, [currentUser])

  useEffect(() => {
    if (!isGenerating) return
    const interval = setInterval(() => setDotCount((d) => (d % 3) + 1), 400)
    return () => clearInterval(interval)
  }, [isGenerating])

  const promptCharCount = nonWhitespaceLength(prompt)
  const isValidPromptLength = promptCharCount >= MIN_PROMPT_CHARS && promptCharCount <= MAX_PROMPT_CHARS
  const canGenerate = isValidPromptLength && !isGenerating
  const hasContent = prompt.trim().length > 0

  const handlePromptChange = (value) => {
    if (nonWhitespaceLength(value) > MAX_PROMPT_CHARS) return
    onPromptChange(value)
  }

  const generateChoices = async () => {
    if (!canGenerate) return

    setIsGenerating(true)
    setError(null)
    setWarning(null)

    const requestBody = { prompt, ai_tier: aiTier }

    if (useLocation) {
      const coords = await fetchLocation()
      if (coords) requestBody.user_coords = coords
    }

    const { data, error: apiError } = await postJson(API_ENDPOINTS.GENERATE_CHOICES, requestBody)
    setIsGenerating(false)

    if (apiError) {
      if (data?.reset_at && !data.premium_pass_active) {
        setError(
          <>
            Free daily smart AI request already used. Go{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                onPremiumClick?.()
              }}
            >
              premium
            </a>{" "}
            or use quick without location.
          </>
        )
      } else {
        setError(apiError)
      }
      return
    }

    if (data.warning) setWarning(data.warning)

    onChoicesGenerated?.(data.choices.slice(0, maxChoices).join("\n"))
    onPromptUsed?.(prompt)
    onMarkersReceived?.(data.markers || [], requestBody.user_coords || null)
  }

  return (
    <>
      <div className="subsection-label">AI Generate</div>
      <textarea
        name="ai-prompt"
        className="input-base choices-generate-input"
        rows="2"
        placeholder="Describe what you want and let AI fill the wheel!"
        value={prompt}
        onChange={(e) => handlePromptChange(e.target.value)}
        disabled={isGenerating}
      />
      <div className="intelligence-row">
        <select
          name="ai-tier"
          className="input-base intelligence-tier-select"
          value={aiTier}
          onChange={(e) => onAiTierChange(e.target.value)}
          title="Select AI tier"
        >
          <option value="quick">Quick</option>
          <option value="smart" disabled={!isLoggedIn}>
            Smart{!isLoggedIn ? " (login required)" : ""}
          </option>
        </select>
        <HintButton active={showTierHint} onClick={() => setShowTierHint((v) => !v)} label="AI tier info" />
        <label
          className={`intelligence-location-label ${!isLoggedIn ? "intelligence-location-disabled" : ""}`}
          title={isLoggedIn ? "Use precise location for local suggestions" : LOGIN_REQUIRED_TITLE}
        >
          <input
            name="use-location"
            type="checkbox"
            checked={useLocation}
            onChange={(e) => onUseLocationChange?.(e.target.checked)}
            disabled={!isLoggedIn}
            className="intelligence-location-checkbox"
          />
          <svg className="intelligence-location-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
          </svg>
          <span className="intelligence-location-text">Location</span>
        </label>
        <HintButton active={showLocationHint} onClick={() => setShowLocationHint((v) => !v)} label="Location info" />
      </div>
      {showTierHint && (
        <div className="intelligence-hint-message">
          Quick is fast and simple. Smart thinks harder and searches the web.
        </div>
      )}
      {showLocationHint && (
        <div className="intelligence-hint-message">Uses your precise location for nearby, relevant suggestions.</div>
      )}
      <div className="choices-generate-actions">
        <button
          className="btn-base btn-secondary choices-input-btn"
          onClick={() => onPromptChange("")}
          disabled={!hasContent || isGenerating}
          title="Clear prompt"
        >
          Clear
        </button>
        <button
          className="btn-base btn-secondary choices-input-btn"
          onClick={generateChoices}
          disabled={!canGenerate}
          title="Generate choices using AI"
        >
          {isGenerating ? ".".repeat(dotCount) : "Generate"}
        </button>
      </div>
      {warning && <div className="warning-message">{warning}</div>}
      {error && <div className="error-message">{error}</div>}
    </>
  )
}

export default AiPrompt
